import { useState } from 'react'
import { motion } from 'framer-motion'
import { Button, Typography, Box, Tooltip, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

const SlideItem = ({
  src,
  title,
  description,
  after,
  index,
  onClick,
  isSelected,
  navigate,
}) => {
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)

  // 📱 DETECTAR TAMAÑO DE PANTALLA PARA RESPONSIVIDAD
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ scale: isSelected ? 1.02 : 1.07 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        // 🔧 PROBLEMA PRINCIPAL: ANCHO RESPONSIVE EN LUGAR DE FIJO
        width: isSmallMobile ? 200 : isMobile ? 220 : 250, // Antes era fijo: 250
        // 📏 ALTURA RESPONSIVE PARA MEJOR USO DEL ESPACIO
        height: isSmallMobile ? 320 : isMobile ? 350 : 400, // Antes era fijo: 400
        borderRadius: isMobile ? 16 : 20, // Bordes más pequeños en móvil
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
        cursor: 'pointer',
        backgroundColor: '#000',
        transition: 'border 0.3s ease, box-shadow 0.3s ease',
        // 🎯 MEJORA: SOMBRAS PARA MEJOR SEPARACIÓN VISUAL
        boxShadow: isSelected
          ? '0 8px 24px rgba(76, 175, 80, 0.3)'
          : '0 4px 12px rgba(0, 0, 0, 0.15)',
      }}>
      {/* Imagen */}
      <img
        src={src}
        alt={`img-${index}`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />

      {/* Capa oscura con mejor gradiente */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: isSelected
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.75))'
            : isHovered
            ? 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.65))'
            : 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7))',
          zIndex: 1,
        }}
      />

      {/* Contenido con mejor responsive */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          zIndex: 2,
          // 📱 PADDING RESPONSIVE PARA MEJOR USO DEL ESPACIO
          padding: isSmallMobile ? '16px 12px' : isMobile ? '20px 16px' : '24px 20px',
        }}>
        {/* Título con tamaño responsive */}
        <Typography
          variant={isSmallMobile ? 'subtitle1' : isMobile ? 'h6' : 'h6'}
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.7)',
            color: isSelected ? '#ffffff' : theme.palette.secondary.light,
            mb: isSmallMobile ? 1 : 2,
            // 📝 MEJOR LEGIBILIDAD EN MÓVILES
            fontSize: isSmallMobile ? '0.95rem' : isMobile ? '1.1rem' : '1.25rem',
            lineHeight: 1.2,
            // 🎯 LÍMITE DE LÍNEAS PARA EVITAR TEXTOS MUY LARGOS
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
          {title}
        </Typography>

        {/* Descripción con mejor responsive */}
        <Box
          sx={{
            width: '100%',
            // 📱 PADDING HORIZONTAL RESPONSIVE
            px: isSmallMobile ? 0.5 : isMobile ? 1 : 2,
          }}>
          <Typography
            variant="body2"
            sx={{
              p: isSmallMobile ? 0.8 : isMobile ? 1 : 1,
              color: isSelected ? '#ffffff' : '#fff',
              bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : 'rgba(20,23,12,0.65)',
              backdropFilter: !isSelected ? 'blur(2px)' : 'blur(4px)',
              // 📝 TAMAÑO DE FUENTE RESPONSIVE
              fontSize: isSmallMobile ? '0.75rem' : isMobile ? '0.8rem' : '0.9rem',
              fontWeight: isSelected ? '600' : '500',
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.8)',
              mb: isSmallMobile ? 2 : 3,
              borderRadius: 2,
              lineHeight: 1.3,
              // 🎯 LÍMITE DE LÍNEAS PARA DESCRIPCIONES LARGAS
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 3 : 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
            {description}
          </Typography>
        </Box>

        {/* Botones/Estado con mejor responsive */}
        {after ? (
          <Tooltip title="Disponible en próximas actualizaciones">
            <span>
              <Box
                sx={{
                  backgroundColor: '#ff9800',
                  color: '#fff',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  px: isSmallMobile ? 1.5 : 2,
                  py: 0.5,
                  // 📝 TAMAÑO DE FUENTE RESPONSIVE PARA BADGES
                  fontSize: isSmallMobile ? '0.65rem' : '0.75rem',
                  mt: 1,
                  textAlign: 'center',
                }}>
                PRÓXIMAMENTE
              </Box>
            </span>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            onClick={e => {
              e.stopPropagation()
              navigate?.()
            }}
            sx={{
              backgroundColor: '#4caf50',
              fontWeight: 'bold',
              // 📱 PADDING RESPONSIVE PARA BOTONES
              px: isSmallMobile ? 2 : 3,
              py: isSmallMobile ? 0.8 : 1,
              borderRadius: 50,
              // 📝 TAMAÑO DE FUENTE RESPONSIVE PARA BOTONES
              fontSize: isSmallMobile ? '0.7rem' : isMobile ? '0.8rem' : '0.9rem',
              minHeight: isSmallMobile ? 32 : 36,
              '&:hover': {
                backgroundColor: '#388e3c',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(56, 142, 60, 0.4)',
              },
              // 🎯 TRANSICIONES SUAVES
              transition: 'all 0.2s ease',
            }}>
            {isSmallMobile ? 'EXPLORAR' : 'EXPLORAR EL MAPA'}
          </Button>
        )}
      </div>
    </motion.div>
  )
}

export default SlideItem

/*
🎯 MEJORAS IMPLEMENTADAS PARA MÓVILES:

📱 RESPONSIVE SIZING:
- Ancho: 200px (móvil pequeño) → 220px (móvil) → 250px (desktop)
- Alto: 320px (móvil pequeño) → 350px (móvil) → 400px (desktop)
- Bordes: 16px (móvil) → 20px (desktop)

📝 TIPOGRAFÍA OPTIMIZADA:
- Títulos con límite de 2 líneas
- Descripciones con límite de 3-4 líneas
- Tamaños de fuente escalables por dispositivo

🎨 VISUAL IMPROVEMENTS:
- Mejores sombras para separación visual
- Gradientes optimizados para legibilidad
- Paddings responsivos en contenido

🔧 PROBLEMAS SOLUCIONADOS:
1. ❌ Ancho fijo causaba separación excesiva → ✅ Ancho responsive
2. ❌ Altura fija desperdiciaba espacio → ✅ Altura adaptable
3. ❌ Textos muy grandes en móviles → ✅ Tipografía escalable
4. ❌ Paddings excesivos → ✅ Espaciado optimizado
5. ❌ Botones muy grandes → ✅ Botones compactos pero funcionales

🎯 PARA AJUSTAR SEPARACIÓN ENTRE CARDS:
Ve al componente padre y ajusta el margin entre cards en:
- margin: '0 4px' (más juntas) o '0 12px' (más separadas)
*/
